import { useCallback, useEffect, useRef } from 'react';
import Config from '../../../utils/Config';

// Meta's "Instagram API with Instagram Login" product uses Instagram's own
// OAuth dialog (instagram.com), not Facebook Login — so this hook mirrors
// useMetaLogin.js's popup + auth-code shape, but points at Instagram's OAuth
// endpoint and uses a distinct postMessage source/state channel so the two
// hooks/callbacks never cross-talk if both popups were somehow open at once.
//   1. Open a popup pointed at Instagram's OAuth dialog (response_type=code).
//   2. The popup lands on /oauth/instagram/callback (see
//      pages/OAuthCallback/InstagramOAuthCallback.jsx), which reads `code`
//      off the query string and postMessages it back to this window, then
//      closes.
//   3. This hook resolves onSuccess({ code, redirect_uri }) — redirect_uri
//      is returned too because cq-api's save-access-tokens endpoint requires
//      the exact redirect_uri used to obtain the code.
//
// This mirrors useGoogleLogin's { onSuccess, onError, scope } call shape used
// throughout components/Datasources/Google*Auth so it can be dropped into the
// same component structure.

const INSTAGRAM_OAUTH_DIALOG_URL = 'https://www.instagram.com/oauth/authorize';
const INSTAGRAM_OAUTH_MESSAGE_SOURCE = 'instagram-oauth';

const getRedirectUri = () => `${window.location.origin}/oauth/instagram/callback`;

const useInstagramLogin = ({ onSuccess, onError, scope }) => {
  const popupRef = useRef(null);
  const listenerRef = useRef(null);

  const cleanup = useCallback(() => {
    if (listenerRef.current) {
      window.removeEventListener('message', listenerRef.current);
      listenerRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const login = useCallback(() => {
    if (!Config.INSTAGRAM_APP_ID) {
      onError?.(new Error(
        'REACT_APP_INSTAGRAM_APP_ID is not configured on the client — ' +
        'set it to the distinct Instagram App ID shown under "API setup ' +
        'with Instagram login" in the Meta Developer dashboard (separate ' +
        'from the Facebook App ID used for Facebook Pages/Meta Ads), and ' +
        'match cq-api\'s INSTAGRAM_APP_ID.'
      ));
      return;
    }

    cleanup();

    const redirectUri = getRedirectUri();
    const state = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;

    // Note: unlike useMetaLogin, no `auth_type=rerequest` param is included
    // here — Instagram's own OAuth dialog does not necessarily support the
    // same param set as Facebook's dialog, and this wasn't confirmed against
    // Instagram's docs during this change. Flagged as an open question in
    // the completion summary rather than guessed.
    //
    // client_id here is INSTAGRAM_APP_ID, not META_APP_ID — Instagram Login's
    // OAuth dialog requires its own distinct app ID (see Config.js).
    const params = new URLSearchParams({
      client_id: Config.INSTAGRAM_APP_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      state,
      ...(scope ? { scope } : {}),
    });

    popupRef.current = window.open(
      `${INSTAGRAM_OAUTH_DIALOG_URL}?${params.toString()}`,
      'instagram-oauth',
      'width=600,height=700'
    );

    if (!popupRef.current) {
      onError?.(new Error('Instagram OAuth popup was blocked by the browser.'));
      return;
    }

    listenerRef.current = (event) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.source !== INSTAGRAM_OAUTH_MESSAGE_SOURCE) return;
      if (event.data.state && event.data.state !== state) return;

      cleanup();
      popupRef.current?.closed === false && popupRef.current.close();

      if (event.data.code) {
        onSuccess?.({ code: event.data.code, redirect_uri: redirectUri });
      } else {
        onError?.(event.data.error || new Error('Instagram OAuth did not return a code.'));
      }
    };

    window.addEventListener('message', listenerRef.current);
  }, [cleanup, onSuccess, onError, scope]);

  return login;
};

export default useInstagramLogin;
